import { useState, useEffect } from 'react';

export default function Home() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToEstimate = () => {
    window.open('https://book.finneganslandscapingmd.com/widget/bookings/get-estimate-booked', '_blank');
  };

  const services = [
    {
      title: 'Lawn Care',
      description: 'Professional mowing, edging, cleanups, aeration and overseeding services',
      icon: 'ri-plant-line',
      image: 'https://static.readdy.ai/image/c82a80d6d696663c2db3260ddbcff61b/c5dc8e0652a09e4ff10e7acfc2ada00e.png'
    },
    {
      title: 'Landscaping and Mulch',
      description: 'Expert mulch installation, edging, planting, and decorative rock installations',
      icon: 'ri-seedling-line',
      image: 'https://static.readdy.ai/image/c82a80d6d696663c2db3260ddbcff61b/c457080ec7463805e9698ebc1d3be630.png'
    },
    {
      title: 'Hardscapes and Projects',
      description: 'Quality retaining walls, stone steps, shed pads, and small concrete work',
      icon: 'ri-building-line',
      image: 'https://static.readdy.ai/image/c82a80d6d696663c2db3260ddbcff61b/895eedce34d54de9a3793baff11d7457.png'
    },
    {
      title: 'Shrubs and Trees',
      description: 'Expert trimming, pruning, removal, and complete bed cleanouts',
      icon: 'ri-tree-line',
      image: 'https://public.readdy.ai/ai/img_res/59b9bb6d43d4ab1e3bc70356431d6d9a.jpg'
    },
    {
      title: 'Power Washing',
      description: 'Deep cleaning for driveways, walkways, patios, and home siding',
      icon: 'ri-brush-line',
      image: 'https://static.readdy.ai/image/c82a80d6d696663c2db3260ddbcff61b/d4a6321a25bcf2432eb4679d8d0a4e60.png'
    },
    {
      title: 'Snow Removal',
      description: 'Reliable residential snow plowing, driveway clearing, and light commercial service',
      icon: 'ri-snowy-line',
      image: 'https://static.readdy.ai/image/c82a80d6d696663c2db3260ddbcff61b/fe95821e29ad4e390d852bb2eab275c9.png'
    }
  ];

  const whyChoosePoints = [
    {
      title: 'Local and Family Owned',
      description: 'Established in 2015, we\'re a trusted family business serving our Harford County community',
      icon: 'ri-home-heart-line'
    },
    {
      title: 'All Around Service Provider',
      description: 'Complete outdoor services from lawn care to hardscaping - your one-stop landscaping solution',
      icon: 'ri-tools-line'
    },
    {
      title: 'Free Estimates & Clear Communication',
      description: 'Transparent pricing with detailed estimates and clear communication throughout every project',
      icon: 'ri-chat-smile-line'
    },
    {
      title: 'Quality Work at Fair Pricing',
      description: 'Professional results that exceed expectations while maintaining competitive and fair pricing',
      icon: 'ri-star-line'
    }
  ];

  const serviceAreas = [
    'Aberdeen', 'Abingdon', 'Bel Air', 'Edgewood', 
    'Joppa', 'Havre de Grace', 'Forest Hill', 'Fallston'
  ];

  const galleryImages = [
    {
      before: 'https://readdy.ai/api/search-image?query=overgrown%20lawn%20and%20landscaping%20before%20professional%20service%20in%20Aberdeen%20Maryland%20residential%20property%2C%20unmaintained%20yard%20with%20weeds%20and%20tall%20grass&width=300&height=200&seq=before-001&orientation=landscape',
      after: 'https://readdy.ai/api/search-image?query=beautiful%20maintained%20lawn%20after%20professional%20landscaping%20service%20in%20Aberdeen%20MD%2C%20pristine%20green%20grass%20and%20neat%20garden%20beds&width=300&height=200&seq=after-001&orientation=landscape',
      alt: 'Landscaping in Aberdeen MD - Before and After Lawn Care'
    },
    {
      before: 'https://readdy.ai/api/search-image?query=bare%20flower%20beds%20and%20unmaintained%20landscaping%20before%20mulch%20installation%20in%20Harford%20County%20Maryland%20residential%20area&width=300&height=200&seq=before-002&orientation=landscape',
      after: 'https://readdy.ai/api/search-image?query=professionally%20installed%20mulch%20beds%20with%20beautiful%20plants%20and%20landscaping%20in%20Harford%20County%20lawn%20care%20transformation&width=300&height=200&seq=after-002&orientation=landscape',
      alt: 'Harford County Lawn Care - Mulch Installation Before and After'
    },
    {
      before: 'https://readdy.ai/api/search-image?query=old%20damaged%20retaining%20wall%20and%20hardscape%20area%20before%20professional%20renovation%20in%20Aberdeen%20Maryland%20backyard&width=300&height=200&seq=before-003&orientation=landscape',
      after: 'https://readdy.ai/api/search-image?query=beautiful%20new%20stone%20retaining%20wall%20and%20hardscape%20project%20completed%20by%20landscaping%20professionals%20in%20Aberdeen%20MD&width=300&height=200&seq=after-003&orientation=landscape',
      alt: 'Landscaping in Aberdeen MD - Hardscape Retaining Wall Project'
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Sticky Top Bar */}
      <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <div className="flex items-center space-x-3">
              <div className="w-14 h-14 flex items-center justify-center">
                <img 
                  src="https://public.readdy.ai/ai/img_res/fd15517e-3ec4-47ba-9da8-8a99d19633bc.png" 
                  alt="Finnegan's Landscaping LLC Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <span className={`text-xl md:text-2xl font-bold transition-colors duration-300 ${
                isScrolled ? 'text-green-700' : 'text-white'
              }`} style={{fontFamily: 'Playfair Display, serif'}}>
                Finnegan's Landscaping
              </span>
            </div>
            <a 
              href="tel:443-987-8401" 
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 md:px-6 md:py-3 rounded-lg font-semibold text-sm md:text-base transition-colors duration-200 whitespace-nowrap"
            >
              Call Now: 443-987-8401
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://static.readdy.ai/image/c82a80d6d696663c2db3260ddbcff61b/12f552a0783719bfa1a5e1c0c5405598.png')`
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 w-full max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Landscaping and Lawn Care in Aberdeen and Harford County MD
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
            Family owned landscaping company providing lawn care, landscaping, hardscaping, power washing, and snow removal.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a 
              href="tel:443-987-8401" 
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors duration-200 whitespace-nowrap"
            >
              Call Now
            </a>
            <button 
              onClick={scrollToEstimate}
              className="bg-white hover:bg-gray-100 text-green-700 px-8 py-4 rounded-lg font-bold text-lg transition-colors duration-200 whitespace-nowrap"
            >
              Request Free Estimate
            </button>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-green-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center text-center md:text-left space-y-4 md:space-y-0 md:space-x-8">
            <div className="flex items-center space-x-2">
              <i className="ri-shield-check-line text-green-600 text-xl"></i>
              <span className="text-gray-700 font-medium">HomeAdvisor Screened and Approved</span>
            </div>
            <div className="flex items-center space-x-2">
              <i className="ri-heart-line text-green-600 text-xl"></i>
              <span className="text-gray-700 font-medium">Neighborhood Favorite on Nextdoor</span>
            </div>
            <div className="flex items-center space-x-2">
              <i className="ri-star-fill text-yellow-500 text-xl"></i>
              <span className="text-gray-700 font-medium">Five Star Local Reviews</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Complete Landscaping Services
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              From routine lawn care to complex hardscaping projects, we provide comprehensive outdoor services for Harford County homes and businesses.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="w-full h-48">
                  <img 
                    src={service.image} 
                    alt={service.title} 
                    className="w-full h-full object-cover"
                    style={{ objectPosition: '50% 20%' }}
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className={`${service.icon} text-2xl text-green-600`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 ml-3">{service.title}</h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Why Choose Finnegan's Landscaping
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Trusted by Aberdeen and Harford County residents since 2015, we deliver exceptional results with personal service you can count on.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {whyChoosePoints.map((point, index) => (
              <div key={index} className="flex items-start space-x-4 p-6 bg-white rounded-xl shadow-md">
                <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-lg flex-shrink-0">
                  <i className={`${point.icon} text-2xl text-green-600`}></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{point.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{point.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Serving Harford County Maryland
            </h2>
            <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-4xl mx-auto">
              If you need a landscaper in Harford County MD, contact Finnegan's Landscaping for a fast free estimate. We proudly serve these communities:
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {serviceAreas.map((area, index) => (
              <div key={index} className="bg-green-50 rounded-lg p-4 text-center">
                <span className="text-lg font-semibold text-green-700">{area}</span>
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a 
                href="tel:443-987-8401" 
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200 whitespace-nowrap"
              >
                Call 443-987-8401
              </a>
              <a 
                href="mailto:harryfinnegan37@gmail.com" 
                className="bg-gray-100 hover:bg-gray-200 text-gray-900 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 whitespace-nowrap"
              >
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Project Gallery */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Our Work Speaks for Itself
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              See the transformation we bring to Aberdeen and Harford County properties with our professional landscaping services.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {galleryImages.map((project, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Before & After</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-2">Before</p>
                      <div className="w-full h-32">
                        <img 
                          src={project.before} 
                          alt={project.alt + ' - Before'} 
                          className="w-full h-full object-cover object-top rounded-lg"
                        />
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-2">After</p>
                      <div className="w-full h-32">
                        <img 
                          src={project.after} 
                          alt={project.alt + ' - After'} 
                          className="w-full h-full object-cover object-top rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-green-700">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Transform Your <strong className="text-green-200">Aberdeen MD</strong> Property?
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Contact Harford County's trusted landscaping experts today
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:443-987-8401" 
              className="bg-white text-green-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-green-50 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call Now: 443-987-8401
            </a>
            <a 
              href="https://book.finneganslandscapingmd.com/widget/bookings/get-estimate-booked"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-800 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-green-900 transition-all duration-300 border-2 border-green-400 whitespace-nowrap cursor-pointer"
            >
              Get Free Estimate
            </a>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-4">
            What Our <strong className="text-green-600">Harford County</strong> Customers Say
          </h2>
          <div className="text-center mb-12">
            <div className="flex justify-center items-center gap-4 mb-4">
              <div className="flex text-yellow-400 text-2xl">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill"></i>
                ))}
              </div>
              <span className="text-gray-600 font-medium">5.0 Rating</span>
            </div>
            <div className="flex flex-col sm:flex-row justify-center gap-4 text-sm text-gray-600">
              <div className="flex items-center justify-center gap-2">
                <i className="ri-shield-check-line text-green-600"></i>
                <span>Screened & Approved on HomeAdvisor</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <i className="ri-heart-line text-green-600"></i>
                <span>Neighborhood Favorite on Nextdoor</span>
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="flex text-yellow-400 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill"></i>
                ))}
              </div>
              <p className="text-gray-700 mb-4 italic">
                "Excellent job. Yard looks amazing and very professional."
              </p>
              <div className="text-sm text-gray-500">
                - Verified HomeAdvisor Customer
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="flex text-yellow-400 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill"></i>
                ))}
              </div>
              <p className="text-gray-700 mb-4 italic">
                "Great communication and reliable service. Highly recommend."
              </p>
              <div className="text-sm text-gray-500">
                - Aberdeen MD Resident
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="flex text-yellow-400 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill"></i>
                ))}
              </div>
              <p className="text-gray-700 mb-4 italic">
                "They showed up on time, worked hard, and cleaned everything up."
              </p>
              <div className="text-sm text-gray-500">
                - Harford County Customer
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-12">
            Frequently Asked Questions About <strong className="text-green-600">Aberdeen MD Landscaping</strong>
          </h2>
          
          <div className="space-y-6">
            <div className="bg-green-50 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-800 mb-3">
                <a href="#faq1" className="text-green-600">Do you offer free estimates?</a>
              </h4>
              <p className="text-gray-700">
                Yes, we provide free estimates within our service area for all landscaping and lawn care projects in <strong>Harford County MD</strong>.
              </p>
            </div>
            
            <div className="bg-green-50 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-800 mb-3">
                <a href="#faq2" className="text-green-600">How far do you travel?</a>
              </h4>
              <p className="text-gray-700">
                We serve all of <strong>Harford County</strong>, including Aberdeen, Bel Air, Havre de Grace, and surrounding communities.
              </p>
            </div>
            
            <div className="bg-green-50 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-800 mb-3">
                <a href="#faq3" className="text-green-600">Do you provide residential and commercial service?</a>
              </h4>
              <p className="text-gray-700">
                Yes, we offer both residential and commercial <strong>landscaping services</strong> throughout Aberdeen MD and Harford County.
              </p>
            </div>
            
            <div className="bg-green-50 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-800 mb-3">
                <a href="#faq4" className="text-green-600">Do you accept credit cards?</a>
              </h4>
              <p className="text-gray-700">
                Yes, we accept all major credit cards for your convenience, in addition to cash and check payments.
              </p>
            </div>
            
            <div className="bg-green-50 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-800 mb-3">
                <a href="#faq5" className="text-green-600">Do you handle one time cleanups and ongoing maintenance?</a>
              </h4>
              <p className="text-gray-700">
                Yes, we provide both one-time cleanup services and ongoing maintenance contracts for <strong>lawn care</strong> and landscaping in Harford County.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-16 bg-green-600">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Request Your Free Estimate
            </h2>
            <p className="text-xl text-green-100">
              Tell us about your project and our team will follow up quickly for your <strong className="text-white">Aberdeen MD landscaping</strong> needs.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-xl overflow-hidden" style={{minHeight: '650px'}}>
            <iframe
              src="https://book.finneganslandscapingmd.com/widget/bookings/get-estimate-booked"
              title="Book a Free Estimate - Finnegan's Landscaping"
              width="100%"
              height="650"
              frameBorder="0"
              style={{display: 'block', border: 'none'}}
              allow="payment"
            ></iframe>
          </div>

          <div className="mt-8 text-center space-y-4">
            <a
              href="tel:443-987-8401"
              className="inline-flex items-center justify-center bg-white text-green-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-green-50 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Tap to Call 443-987-8401
            </a>
            <div>
              <a
                href="mailto:harryfinnegan37@gmail.com"
                className="inline-flex items-center justify-center text-white hover:text-green-100 font-medium cursor-pointer"
              >
                <i className="ri-mail-line mr-2"></i>
                harryfinnegan37@gmail.com
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center space-y-6">
            <h3 className="text-2xl font-bold text-green-400">
              Finnegan's Landscaping LLC
            </h3>
            
            <div className="space-y-2">
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <a 
                  href="tel:443-987-8401" 
                  className="text-green-400 hover:text-green-300 cursor-pointer"
                >
                  <i className="ri-phone-line mr-2"></i>
                  443-987-8401
                </a>
                <a 
                  href="mailto:harryfinnegan37@gmail.com" 
                  className="text-green-400 hover:text-green-300 cursor-pointer"
                >
                  <i className="ri-mail-line mr-2"></i>
                  harryfinnegan37@gmail.com
                </a>
              </div>
            </div>
            
            <div className="border-t border-gray-700 pt-6 space-y-4">
              <p className="text-gray-400 text-sm">
                <strong>Finnegan's Landscaping LLC</strong> offers <strong>landscaping and lawn care in Aberdeen and Harford County MD</strong>. 
                Professional services include landscape design, lawn maintenance, mulching, hardscapes, power washing, and <strong>Harford County snow removal</strong>.
              </p>
              
              <div className="flex justify-center">
                <a 
                  href="https://readdy.ai/?origin=logo" 
                  className="text-gray-500 hover:text-gray-400 text-sm cursor-pointer"
                >
                  Website Builder
                </a>
              </div>
              
              <p className="text-gray-500 text-sm">
                © 2024 Finnegan's Landscaping LLC. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
